import { TranscriptEditorChangeType, TranscriptEditorObserver } from '../types';
import { TranscriptEditorCore } from '../TranscriptEditorCore';

/**
 * Notification data structure
 */
interface QueuedNotification {
  changeType: TranscriptEditorChangeType;
  payload?: any;
}

/**
 * Manages observers for the transcript editor
 * 
 * This is a specialized implementation of the Observer pattern for the TranscriptEditor,
 * allowing components to subscribe to specific change types and receive notifications.
 * It supports batching of notifications for improved performance during complex operations.
 */
export class TranscriptEditorObserverManager {
  /** Map of observers by change type */
  private observers: Map<string, TranscriptEditorObserver[]> = new Map();
  
  /** Queue of notifications waiting to be processed */
  private queuedNotifications: QueuedNotification[] = [];

  /**
   * Creates a new ObserverManager
   * @param editor The transcript editor instance
   */
  constructor(private editor: TranscriptEditorCore) {}

  /**
   * Register an observer to receive notifications about changes
   * 
   * @param observer The observer to register
   * @param changeTypes Optional array of specific change types to observe
   */
  registerObserver(
    observer: TranscriptEditorObserver,
    changeTypes?: TranscriptEditorChangeType[]
  ): void {
    if (!changeTypes || changeTypes.length === 0) {
      // Register for all change types
      for (const type of Object.values(TranscriptEditorChangeType)) {
        console.log('registering observer for type', type);
        this.registerObserverForType(type, observer);
      }
    } else {
      // Register for specific change types
      for (const type of changeTypes) {
        this.registerObserverForType(type, observer);
      }
    }
  }

  /**
   * Unregister an observer
   * 
   * @param observer The observer to unregister
   * @param changeTypes Optional array of specific change types to unregister for
   */
  unregisterObserver(
    observer: TranscriptEditorObserver,
    changeTypes?: TranscriptEditorChangeType[]
  ): void {
    if (!changeTypes || changeTypes.length === 0) {
      // Unregister from all change types
      for (const type of Object.values(TranscriptEditorChangeType)) {
        this.unregisterObserverForType(type, observer);
      }
    } else {
      // Unregister from specific change types
      for (const type of changeTypes) {
        this.unregisterObserverForType(type, observer);
      }
    }
  }

  /**
   * Notify all registered observers about a change
   * 
   * @param changeType The type of change
   * @param payload Optional data about the change
   * @param immediate Whether to send immediately even during batching
   */
  notifyObservers(
    changeType: TranscriptEditorChangeType,
    payload?: any,
    immediate: boolean = false
  ): void {
    if (this.editor.isInBatch() && !immediate) {
      // Queue notification for later delivery
      this.queuedNotifications.push({ changeType, payload });
      return;
    }

    // Deliver notification immediately
    this.deliverNotification(changeType, payload);
  }

  /**
   * Process all queued notifications
   * 
   * This should be called at the end of a batch operation
   */
  processQueuedNotifications(): void {
    if (this.queuedNotifications.length === 0) {
      return;
    }

    // Make a copy and clear the queue before processing
    // This prevents issues if notifications trigger additional notifications
    const notifications = [...this.queuedNotifications];
    this.queuedNotifications = [];

    // Process queued notifications
    for (const { changeType, payload } of notifications) {
      this.deliverNotification(changeType, payload);
    }
  }
  
  /**
   * Cleans up resources and removes all observers
   * Should be called when the editor is being destroyed
   */
  cleanup(): void {
    this.observers.clear();
    this.queuedNotifications = [];
  }

  /**
   * Register an observer for a specific change type
   * 
   * @param changeType The change type to register for
   * @param observer The observer to register
   */
  private registerObserverForType(
    changeType: TranscriptEditorChangeType,
    observer: TranscriptEditorObserver
  ): void {
    if (!this.observers.has(changeType)) {
      this.observers.set(changeType, []);
    }

    const observers = this.observers.get(changeType)!;
    if (!observers.includes(observer)) {
      observers.push(observer);
    }
  }

  /**
   * Unregister an observer for a specific change type
   * 
   * @param changeType The change type to unregister from
   * @param observer The observer to unregister
   */
  private unregisterObserverForType(
    changeType: TranscriptEditorChangeType,
    observer: TranscriptEditorObserver
  ): void {
    if (!this.observers.has(changeType)) {
      return;
    }

    const observers = this.observers.get(changeType)!;
    const index = observers.indexOf(observer);
    if (index !== -1) {
      observers.splice(index, 1);
    }
  }

  /**
   * Deliver a notification to registered observers
   * 
   * @param changeType The type of change
   * @param payload Optional data about the change
   */
  private deliverNotification(
    changeType: TranscriptEditorChangeType,
    payload?: any
  ): void {

    if (!this.observers.has(changeType)) {
      return;
    }

    const observers = this.observers.get(changeType)!;
    for (const observer of observers) {
      try {
        observer.onEditorChange(this.editor, changeType, payload);
      } catch (error) {
        console.error('Error in observer:', error);
      }
    }
  }
}